declare const DEBUG:boolean;

require('config/vendor');
require('styles/site.styles.css');

import { 
	StorageToggleServiceProvider, 
	BootstrapService 
} from 'util/services';
import {
	ActionService,
	AuthAction,
	AuthService,
	ProgressService
} from 'phoenix/services';
import { ShareBaseModule} from 'sharebase/sharebase';
import { RealTimeServicesModule } from 'real-time';
import { ShareBaseConfigModule } from 'config/appConfig';
import { componentsModule } from 'sharebase-app/components'
import { datastoreModule } from 'sharebase-app/dataStores';
import { servicesModule } from 'sharebase-app/services';
import { interceptorsModule } from 'sharebase-app/interceptors' ;
import { OfficeOnlineModule } from 'office-online';
import { DocumentDataStore } from 'sharebase-app/dataStores/documentDataStore';
import { ShaasModule } from 'shaas';
import { ThemingService } from 'sharebase/services';
import { shaasParentRoute } from 'shaas/shaas.routes';

let bootstrapService = new BootstrapService('sharebase-app');

bootstrapService.bootstrapApp()
	.catch(() => {
		throw new Error('ShareBase was unable to be loaded');
	});

class AppController {
	public static $inject =  [
		'pxAuthService',
		'pxProgressService',
		'pxEnvironmentService',
		'pxActionService',
		'pxAuthInterceptor',
		'sbThemingService'
	];

	public constructor(
		public readonly authService:AuthService,
		public readonly progressUI:ProgressService,
		public readonly environmentService,
		pxActionService:ActionService,
		pxAuthInterceptor,
		public readonly sbThemingService:ThemingService
	) {
		pxActionService.subscribe(AuthAction.AuthStateChanged, () => {
			pxAuthInterceptor.redirectPostLogin();
		});
	}
}

angular
	.module('sharebase-app', [
		'ngMaterial',
		'ngMessages',
		'ui.router',
		interceptorsModule.name,
		servicesModule.name,
		datastoreModule.name,
		componentsModule.name,
		OfficeOnlineModule.name,
		RealTimeServicesModule.name,
		ShareBaseModule.name,
		ShareBaseConfigModule.name,
		ShaasModule.name
	])
	.config([
		'$httpProvider',
		'$locationProvider',
		'$mdThemingProvider',
		'$urlRouterProvider',
		'$stateProvider',
		'$compileProvider',
		'$provide',
		'storageToggleServiceProvider',
		'debugInfoEnabled',	(
			$httpProvider:ng.IHttpProvider,
			$locationProvider:ng.ILocationProvider,
			$mdThemingProvider:ng.material.IThemingProvider,
			$urlRouterProvider:ng.ui.IUrlRouterProvider,
			$stateProvider:ng.ui.IStateProvider,
			$compileProvider:ng.ICompileProvider,
			$provide,
			storageToggleServiceProvider:StorageToggleServiceProvider,
			debugInfoEnabled:boolean
		) => {
			storageToggleServiceProvider.setKey('sb-toggles');
			//bind debug info to DOM
			$compileProvider.debugInfoEnabled(debugInfoEnabled);
			if(debugInfoEnabled){	
				//add 500 error logger				
				$httpProvider.interceptors.push('fiveHundredErrorInterceptor');
			}

			//Re-enable this so that controllers can access bindings injected via 'bindings', 'locals', or 'bindToController'
			//in the constructor.  Otherwise, they are not hydrated until the angular $onInit method.
			//Documentation of the change from 1.5.x: https://docs.angularjs.org/guide/migration#-compile-
			//TODO: remove this when constructor logic has been moved to the $onInit function
			$compileProvider.preAssignBindingsEnabled(true);
			
			//Remove the default hasPrefix in angular 1.6.x: '!'
			//Documentation of the change from 1.5.x: https://docs.angularjs.org/guide/migration#commit-aa077e8
			$locationProvider.hashPrefix('');

			//http intercepetors
			$httpProvider.interceptors.push('pxAuthInterceptor');
			$httpProvider.interceptors.push('xSharebaseAppInterceptor');
			$httpProvider.interceptors.push('pxAppIdentifierInterceptor');
			$httpProvider.interceptors.push('sbaPinTokenInterceptor');

			// default state
			$urlRouterProvider.otherwise('/');

			//state definitions
			$stateProvider
				.state('content', {
					abstract: true,
					url: '/',
					template: '<sba-app />'
				})
				.state('loginComponent', {
					abstract : true,
					// this needs to be defined to pass data not on the query string/route
					template: '<sb-login />'
				})
				.state('sharedLinkAuth', {
					url: '/authorize-link',
					params: {
						invalidPassword: false,
						token: '',
						itemType: null,
						itemId: null,
						recipient: null
					},
					template: params => {
						return '<sba-authorize-link-share flex '
							+ 'item-id="' + params.itemId + '" '
							+ 'item-type="' + params.itemType + '" '
							+ 'token="' + params.token + '" '
							+ 'recipient="' + params.recipient + '" '
							+ 'invalid-password="' + params.invalidPassword + '">'
							+ '</sba-authorize-link-shared>';
					},
					data: {
						rule: ['$state', '$stateParams', ($state, $stateParams) => {
							if(!$stateParams.toState.token) {
								return {
									to: 'login'
								};
							}
						}],
						allowUnauthenticatedAccess: true
					}
				})
				.state('myAccount', {
					url: '/my-account',
					template: '<px-my-account />'
				})
				.state('register', {
					url: '/register/:deploymentId?auth',
					template: params => {
						return '<sb-register deployment-id="' + params.deploymentId + '" auth-token="' + params.auth + '"></sb-register>';
					},
					data: {
						allowUnauthenticatedAccess: true
					}
				})
				.state('officeOnline', {
					url: '/office-online/:documentId',
					template: '<wopi-viewer document="$resolve.document"></wopi-viewer>',
					resolve: {
						document: [
							'$stateParams',
							'sbaDocumentDataStore',
							($stateParams:ng.ui.IStateParamsService, sbaDocumentDataStore:DocumentDataStore) => {
								return sbaDocumentDataStore
									.load($stateParams.documentId)
									.then(() => sbaDocumentDataStore.document);
							}
						]
					}
				})
				.state(shaasParentRoute, {
					abstract: true,
					url: '/shaas',
					template: '<div ui-view layout-fill></div>',
					data: {
						rule: [
							'$stateParams',
							'pxAuthService',
							(
								$stateParams,
								pxAuthService:AuthService
							) => {
								if(!$stateParams.toState.access) {
									return {
										to: 'login'
									};
								}

								pxAuthService.initializeWithBearerToken($stateParams.toState.access)
									.catch(err => {
										pxAuthService.invalidateLogin();
										return {
											to: 'login'
										};
									});
							}
						],
						allowUnauthenticatedAccess: true
					}
				});

			//Allow for creating/modifying themes dynamically in controllers
			$provide.value('mdThemingProvider', $mdThemingProvider);
		}
	])
	.run([
		'gettextCatalog',
		gettextCatalog => {
			if (DEBUG) {
				gettextCatalog.showTranslatedMarkers = true;
			}
	}])
	.constant('SBA_TOOLTIP_DELAY', 100)
	.controller('AppController', AppController)
	.decorator('mdDatepickerDirective', [
		'$delegate',
		$delegate => {
			let datepickerDirective = $delegate[0];
			let originalLinkFunction = datepickerDirective.link;
			
			datepickerDirective.compile = function(tElem, tAttrs) {
				//Open all datepicker controls on focus so we don't allow manual input of dates
				tAttrs.mdOpenOnFocus = 'true';

				return function newLinkFunction(scope, elem, attrs, ctrl) {
					originalLinkFunction.apply(datepickerDirective, arguments);

					/**
					 * Blur the datepicker input on focus. I have to do this because the datepicker will focus the input
					 * after a date has been selected. Since we block key input, the user will have to explicitly click
					 * away from the input before they can click it again to get the datepicker. Instead of forcing the
					 * user to explictly blur the input, this handler does it for them automatically.
					 */
					let mdDatePickerInput = scope.ctrl.inputElement;
					mdDatePickerInput.addEventListener('focus', () => {
						mdDatePickerInput.blur();
					});

					/**
					 * Blocks user input on the datepicker. This is a temporary solution
					 * until we decide how to handle parsing date strings in arbitrary formats.
					 * Since we default all date pickers to open on focus, this is just an additional
					 * safeguard
					 */
					mdDatePickerInput.addEventListener('keydown', (ev:KeyboardEvent) => {
						if (!['Enter', 'Tab'].includes(ev.key)) {
							ev.preventDefault();
							return false;
						}
					});
				}
			}

			// get rid of the old link function since we return a link function in compile
			delete datepickerDirective.link;

			return $delegate;
		}
	]);

require('../polyfills/');


// WEBPACK FOOTER //
// ./src/sharebase-app/www/app.ts